import { Injectable } from '@angular/core';
import { ListDetailManagementService } from '@app/lists/shared/list-detail-management/services/list-detail-management.service';
import {
  PopoverController,
  ActionSheetController,
  ModalController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState } from '@panamax/app-state';
import { ListsOverlayService } from '../../../../lists/services/lists-overlay.service';
import { ProductAlternativeSearchService } from '../../../../product-detail/services/product-alternative-search.service';
import { ProductDetailTagsService } from '../../../../product-detail/services/product-detail-tags.service';
import { EllipsisClickData } from '@shared/models/usf-product-card.model';
import { ModalService } from '@shared/services/modal/modal.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { ProductCardService } from '@shared/services/product-card/product-card.service';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { UnitConversionHelper } from '../../helper/unit-conversion-helper';
import { UnitOfMeasureLabels } from '../../enum/unit-conversion-enums';
import { IngredientUnitsCalculations } from '@app/mpp/model/recipe-detail-view-model';
import { Store } from '@ngrx/store';
import { RECIPE_ACTIONS } from '@usf/ngrx-tools';

@Injectable({
  providedIn: 'root',
})
export class MppProductCardService extends ProductCardService {
  constructor(
    actionSheetController: ActionSheetController,
    modalController: ModalController,
    popoverController: PopoverController,
    panAppState: PanAppState,
    translateService: TranslateService,
    listsOverlayService: ListsOverlayService,
    modalService: ModalService,
    navigationHelperService: NavigationHelperService,
    productAlternativeSearchService: ProductAlternativeSearchService,
    productDetailTagsService: ProductDetailTagsService,
    listDetailManagementService: ListDetailManagementService,
    analyticsService: ProductAnalyticsService,
    toggleNewSearchService: ToggleNewSearchService,
    private store: Store,
  ) {
    super(
      actionSheetController,
      modalController,
      popoverController,
      panAppState,
      translateService,
      listsOverlayService,
      modalService,
      navigationHelperService,
      productAlternativeSearchService,
      productDetailTagsService,
      listDetailManagementService,
      analyticsService,
      toggleNewSearchService,
    );
  }

  constructEllipsisButtons(
    ellipsisClickData: EllipsisClickData,
    closeFunc?: Function,
  ) {
    let buttons = [];
    let quickViewButton = this.getQuickViewBtn(ellipsisClickData);
    if (ellipsisClickData.isTouch) {
      const quickHandler = quickViewButton.handler;
      quickViewButton.handler = () => {
        quickHandler();
        closeFunc();
      };
    }
    buttons.push(quickViewButton);
    if (this.shouldAddProductStatus(ellipsisClickData.product)) {
      let productStatusButton = this.getProductStatusBtn(ellipsisClickData);
      if (ellipsisClickData.isTouch) {
        const statusHandler = productStatusButton.handler;
        productStatusButton.handler = () => {
          statusHandler();
          closeFunc();
        };
      }
      buttons.push(productStatusButton);
    }
    this.getSellerQuickViewBtn(buttons, ellipsisClickData);
    let sellerViewButton = buttons[buttons.length - 1];
    if (
      sellerViewButton?.text?.includes('Seller') ||
      (sellerViewButton?.text?.includes('Vista') && ellipsisClickData.isTouch)
    ) {
      const sellerHandler = sellerViewButton.handler;
      sellerViewButton.handler = () => {
        sellerHandler();
        closeFunc();
      };
    }

    return buttons;
  }

  constructBaseUOMOptionsButtons(
    options: string[],
    calculations: IngredientUnitsCalculations,
    isTouch: boolean,
  ) {
    let buttons = [];
    options.forEach(option => {
      buttons.push({
        text: this.translateService.instant(
          UnitConversionHelper.getDisplayFriendlyUOM(
            option as UnitOfMeasureLabels,
          ),
        ),
        handler: () => {
          this.updateBaseUnitOfMeasure(option, calculations);
          this.dismissProductCardOptions(isTouch);
        },
      });
    });
    return buttons;
  }
  constructConversiondUOMOptionsButtons(
    options: string[],
    calculations: IngredientUnitsCalculations,
    isTouch: boolean,
  ) {
    let buttons = [];
    options.forEach(option => {
      buttons.push({
        text: this.translateService.instant(
          UnitConversionHelper.getDisplayFriendlyUOM(
            option as UnitOfMeasureLabels,
          ),
        ),
        handler: () => {
          this.updateConversionUnitOfMeasure(option, calculations);
          this.dismissProductCardOptions(isTouch);
        },
      });
    });
    return buttons;
  }

  constructFinalUOMOptionsButtons(
    options: string[],
    calculations: IngredientUnitsCalculations,
    isTouch: boolean,
  ) {
    let buttons = [];
    options.forEach(option => {
      buttons.push({
        text: this.translateService.instant(
          UnitConversionHelper.getDisplayFriendlyUOM(
            option as UnitOfMeasureLabels,
          ),
        ),
        handler: () => {
          this.updateFinalUnitOfMeasure(option, calculations);
          this.dismissProductCardOptions(isTouch);
        },
      });
    });
    return buttons;
  }

  updateFinalUnitOfMeasure(
    unit: string,
    calculations: IngredientUnitsCalculations,
  ) {
    this.store.dispatch(
      RECIPE_ACTIONS.updateCalculations({
        calculations: { ...calculations, testUnit: unit },
      }),
    );
  }

  updateBaseUnitOfMeasure(
    unit: string,
    calculations: IngredientUnitsCalculations,
  ) {
    this.store.dispatch(
      RECIPE_ACTIONS.updateCalculations({
        calculations: { ...calculations, baseUnit: unit },
      }),
    );
  }

  updateConversionUnitOfMeasure(
    unit: string,
    calculations: IngredientUnitsCalculations,
  ) {
    this.store.dispatch(
      RECIPE_ACTIONS.updateCalculations({
        calculations: { ...calculations, conversionUnit: unit, testUnit: unit },
      }),
    );
  }
}
